/**
 * Onboarding — First-run agent registration flow
 */

import { AgentIdentity } from './agent-identity.js';
import { skipAllowed } from './auth-gate.js';

// SESSION-scoped skip flag (owner decision 2026-07-12): skipping must be re-done every visit.
// Lives in sessionStorage so it survives intake→results navigation within a tab but dies with
// it. Key name shared with auth-gate.js's SKIP_KEY so both skips behave identically.
const SKIP_KEY = 'wq_skip_registration_local';

function getSkipQueryParam() {
  const search = window.location?.search || '';
  const params = new URLSearchParams(search);
  return params.get('skipRegistration') === '1';
}

function getStoredSkipFlag() {
  try {
    return sessionStorage.getItem(SKIP_KEY) === '1';
  } catch {
    return false;
  }
}

function setStoredSkipFlag(enabled) {
  try {
    if (enabled) {
      sessionStorage.setItem(SKIP_KEY, '1');
    } else {
      sessionStorage.removeItem(SKIP_KEY);
    }
  } catch {
    // Ignore storage failures in restricted environments
  }
}

function shouldSkipOnboarding() {
  // Sign-in is required on the live site (auth-gate.js skipAllowed): skipping works locally only.
  if (!skipAllowed(window.location?.hostname)) return false;
  if (getSkipQueryParam()) {
    setStoredSkipFlag(true);
    return true;
  }

  return getStoredSkipFlag();
}

/**
 * Check if onboarding is needed and show modal if so
 * @param {{ force?: boolean }} [options] - force:true shows the modal even when
 *   the skip flag is set (use for explicit user actions like "My Quotes" so a
 *   prior skip stays recoverable).
 * @returns {boolean} true if onboarding modal was shown
 */
export function checkOnboarding(options = {}) {
  const { force = false } = options;
  if (!force && shouldSkipOnboarding()) return false;
  if (AgentIdentity.isRegistered()) return false;
  showOnboardingModal();
  return true;
}

/**
 * Show the onboarding modal
 */
function showOnboardingModal() {
  const modal = document.getElementById('wq-onboarding-modal');
  if (!modal) return;
  modal.classList.add('wq-modal--open');
  document.addEventListener('keydown', onboardingEscHandler);
  const firstInput = modal.querySelector('input');
  if (firstInput) firstInput.focus();
}

function onboardingEscHandler(e) {
  if (e.key === 'Escape') hideOnboardingModal();
}

/**
 * Hide the onboarding modal
 */
function hideOnboardingModal() {
  const modal = document.getElementById('wq-onboarding-modal');
  if (!modal) return;
  modal.classList.remove('wq-modal--open');
  document.removeEventListener('keydown', onboardingEscHandler);
}

function skipOnboarding() {
  setStoredSkipFlag(true);
  hideOnboardingModal();
}

/**
 * Handle onboarding form submission
 * @param {Event} e - Form submit event
 */
export async function handleOnboardingSubmit(e) {
  e.preventDefault();
  const form = e.target;
  const submitBtn = form.querySelector('[type="submit"]');

  const data = {
    npn: form.querySelector('#wq-onboard-npn').value.trim(),
    name: form.querySelector('#wq-onboard-name').value.trim(),
    email: form.querySelector('#wq-onboard-email').value.trim(),
    phone: form.querySelector('#wq-onboard-phone').value.trim(),
    title: form.querySelector('#wq-onboard-title').value.trim() || 'Licensed Insurance Agent',
    tagline: form.querySelector('#wq-onboard-tagline').value.trim() || '',
  };

  // Validate NPN (7-10 digits)
  if (!/^\d{7,10}$/.test(data.npn)) {
    showFieldError('wq-onboard-npn', 'NPN must be 7-10 digits');
    return;
  }

  if (!data.name) {
    showFieldError('wq-onboard-name', 'Name is required');
    return;
  }

  if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    showFieldError('wq-onboard-email', 'Valid email is required');
    return;
  }

  submitBtn.disabled = true;
  submitBtn.textContent = 'Registering...';

  try {
    const profile = await AgentIdentity.registerAgent(data);

    // Update existing agentBranding global for backward compatibility
    if (typeof window.agentBranding !== 'undefined') {
      window.agentBranding = AgentIdentity.toBranding(profile);
      if (typeof window.applyAgentBranding === 'function') {
        window.applyAgentBranding();
      }
    }

    hideOnboardingModal();
  } catch (err) {
    const errorEl = form.querySelector('.wq-onboard-error');
    if (errorEl) {
      errorEl.textContent = err.message;
      errorEl.style.display = 'block';
    }
  } finally {
    submitBtn.disabled = false;
    submitBtn.textContent = 'Get Started';
  }
}

function showFieldError(fieldId, message) {
  const field = document.getElementById(fieldId);
  if (!field) return;
  const errorEl = field.parentElement.querySelector('.wq-field-error');
  if (errorEl) {
    errorEl.textContent = message;
    errorEl.style.display = 'block';
  }
  field.focus();
}

/**
 * Initialize onboarding — call on page load
 */
export function init() {
  const form = document.getElementById('wq-onboarding-form');
  if (form) {
    form.addEventListener('submit', handleOnboardingSubmit);
  }

  const skipBtn = document.getElementById('wq-onboard-skip');
  if (skipBtn && skipAllowed(window.location?.hostname)) {
    skipBtn.style.display = 'block';
    skipBtn.addEventListener('click', skipOnboarding);
  }

  return checkOnboarding();
}

export const Onboarding = {
  checkOnboarding,
  handleOnboardingSubmit,
  init,
  shouldSkipOnboarding,
};
